import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'

/**
 * Empty state — saját SVG illusztrációval (nem csak ikon), barátságos hangvétellel.
 *
 * Variánsok:
 *  - 'invoices' — üres számla-lista (papírköteg + light beam)
 *  - 'search'   — szűrés nem hozott találatot
 *  - 'success'  — minden rendben (pl. nincs eltérés)
 *  - 'data'     — generikus, no-data
 */
export type EmptyVariant = 'invoices' | 'search' | 'success' | 'data'

export function EmptyState({
  title,
  description,
  action,
  variant = 'data',
  className,
  icon,
}: {
  title: string
  description?: string
  action?: ReactNode
  variant?: EmptyVariant
  className?: string
  /** Override illusztráció — ha nem akarod a default-ot. */
  icon?: ReactNode
}) {
  return (
    <div
      className={cn(
        'rounded-xl2 border border-dashed border-slate-200 dark:border-slate-800',
        'bg-white/50 dark:bg-slate-900/30',
        'flex flex-col items-center justify-center text-center px-6 py-12',
        'animate-fade-in-up',
        className,
      )}
    >
      <div className="mb-4 opacity-90">
        {icon ?? <DefaultIllustration variant={variant} />}
      </div>
      <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">{title}</h3>
      {description && (
        <p className="mt-1 max-w-md text-sm text-slate-500 dark:text-slate-400">{description}</p>
      )}
      {action && <div className="mt-4">{action}</div>}
    </div>
  )
}

function DefaultIllustration({ variant }: { variant: EmptyVariant }) {
  if (variant === 'invoices') return <InvoicesIllustration />
  if (variant === 'success') return <SuccessIllustration />
  if (variant === 'search') return <SearchIllustration />
  return <DataIllustration />
}

/** Halom-papír + fény-sugár alulról → "várjuk a számlákat" érzet. */
function InvoicesIllustration() {
  return (
    <svg width="120" height="100" viewBox="0 0 120 100" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <defs>
        <linearGradient id="empt-grad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#eef2ff" />
          <stop offset="100%" stopColor="#e0e7ff" />
        </linearGradient>
        <linearGradient id="empt-beam" x1="0.5" y1="1" x2="0.5" y2="0">
          <stop offset="0%" stopColor="#6366f1" stopOpacity="0.3" />
          <stop offset="100%" stopColor="#6366f1" stopOpacity="0" />
        </linearGradient>
      </defs>
      <ellipse cx="60" cy="92" rx="42" ry="6" fill="url(#empt-beam)" opacity="0.6" />
      <rect x="34" y="28" width="48" height="60" rx="4" fill="url(#empt-grad)" stroke="#c7d2fe" strokeWidth="1" transform="rotate(-6 58 58)" />
      <rect x="38" y="22" width="48" height="62" rx="4" fill="#ffffff" stroke="#a5b4fc" strokeWidth="1.2" transform="rotate(3 62 53)" />
      <line x1="44" y1="36" x2="78" y2="36" stroke="#c7d2fe" strokeWidth="1.5" strokeLinecap="round" transform="rotate(3 62 53)" />
      <line x1="44" y1="44" x2="70" y2="44" stroke="#c7d2fe" strokeWidth="1.5" strokeLinecap="round" transform="rotate(3 62 53)" />
      <line x1="44" y1="52" x2="74" y2="52" stroke="#c7d2fe" strokeWidth="1.5" strokeLinecap="round" transform="rotate(3 62 53)" />
      <line x1="44" y1="60" x2="64" y2="60" stroke="#c7d2fe" strokeWidth="1.5" strokeLinecap="round" transform="rotate(3 62 53)" />
      <rect x="38" y="20" width="48" height="62" rx="4" fill="#ffffff" stroke="#4f46e5" strokeWidth="1.4" />
      <circle cx="74" cy="30" r="4" fill="#10b981" />
      <line x1="44" y1="36" x2="68" y2="36" stroke="#a5b4fc" strokeWidth="2" strokeLinecap="round" />
      <line x1="44" y1="46" x2="78" y2="46" stroke="#e0e7ff" strokeWidth="2" strokeLinecap="round" />
      <line x1="44" y1="56" x2="76" y2="56" stroke="#e0e7ff" strokeWidth="2" strokeLinecap="round" />
      <line x1="44" y1="66" x2="60" y2="66" stroke="#a5b4fc" strokeWidth="2" strokeLinecap="round" />
    </svg>
  )
}

function SuccessIllustration() {
  return (
    <svg width="100" height="100" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <defs>
        <linearGradient id="ok-grad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#d1fae5" />
          <stop offset="100%" stopColor="#a7f3d0" />
        </linearGradient>
      </defs>
      <circle cx="50" cy="50" r="36" fill="url(#ok-grad)" />
      <path d="M36 52 L46 62 L66 40" stroke="#059669" strokeWidth="4.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    </svg>
  )
}

function SearchIllustration() {
  return (
    <svg width="100" height="100" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <circle cx="42" cy="42" r="22" stroke="#a5b4fc" strokeWidth="3" />
      <circle cx="42" cy="42" r="22" fill="#eef2ff" opacity="0.5" />
      <line x1="58" y1="58" x2="76" y2="76" stroke="#4f46e5" strokeWidth="4" strokeLinecap="round" />
      <line x1="32" y1="38" x2="52" y2="38" stroke="#c7d2fe" strokeWidth="2" strokeLinecap="round" />
      <line x1="32" y1="46" x2="48" y2="46" stroke="#c7d2fe" strokeWidth="2" strokeLinecap="round" />
    </svg>
  )
}

function DataIllustration() {
  return (
    <svg width="100" height="100" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <rect x="20" y="20" width="60" height="60" rx="8" fill="#f1f5f9" stroke="#cbd5e1" strokeWidth="1.5" />
      <circle cx="35" cy="38" r="3" fill="#a5b4fc" />
      <line x1="42" y1="38" x2="68" y2="38" stroke="#cbd5e1" strokeWidth="2" strokeLinecap="round" />
      <circle cx="35" cy="50" r="3" fill="#a5b4fc" />
      <line x1="42" y1="50" x2="62" y2="50" stroke="#cbd5e1" strokeWidth="2" strokeLinecap="round" />
      <circle cx="35" cy="62" r="3" fill="#a5b4fc" />
      <line x1="42" y1="62" x2="56" y2="62" stroke="#cbd5e1" strokeWidth="2" strokeLinecap="round" />
    </svg>
  )
}
